<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from '@/components/Button.vue'
import Select from '@/components/form-fields/Select.vue'
import QuantityControl from '@/components/QuantityControl.vue'
import formatMoney from '@/lib/formatters/money'
import type { Product } from '@/lib/types/Product'
import type { PayloadAddToCart } from '@/services/cart'

const props = defineProps<{
  title: string
  product: Product
  isBusy: boolean
  cta: string
}>()

const emit = defineEmits<{
  (e: 'skip'): void
  (e: 'add', payload: PayloadAddToCart): void
}>()

const findDefaultSellingPlan = () => {
  const recurTagValue = props.product.defaultFrequency
  const sellingPlanAllocations =
    props.product.variants[props.product.selectedVariantId]
      .sellingPlanAllocations
  if (!recurTagValue) return sellingPlanAllocations[0].sellingPlan
  const matchingPlan = sellingPlanAllocations.find((spa) =>
    new RegExp(recurTagValue, 'i').test(spa.sellingPlan.name),
  )
  if (matchingPlan) {
    return matchingPlan.sellingPlan
  } else {
    return sellingPlanAllocations[0].sellingPlan
  }
}

const selection = ref<{
  selectedVariantId: string
  selectedSellingPlanId: string | undefined
  quantity: number
}>({
  selectedVariantId: `${props.product.selectedVariantId}`,
  selectedSellingPlanId: `${findDefaultSellingPlan().id}`,
  quantity: 1,
})

const selectedVariant = computed(
  () => props.product.variants[selection.value.selectedVariantId],
)

const price = computed(() => {
  const selectedSellingPlan = selectedVariant.value.sellingPlanAllocations.find(
    (spa) => `${spa.sellingPlan.id}` === selection.value.selectedSellingPlanId,
  )
  if (selectedSellingPlan) {
    return (
      parseInt(selectedVariant.value.price) *
      ((100 - selectedSellingPlan.sellingPlan.price_adjustments[0].value) / 100)
    )
  } else {
    return selectedVariant.value.price
  }
})

const onAdd = () => {
  emit('add', {
    id: props.product.id,
    selectedVariantId: selection.value.selectedVariantId,
    quantity: selection.value.quantity,
    selectedSellingPlanId: selection.value.selectedSellingPlanId,
    attribution: {
      source: 'cart_upsell',
      view: 'additional_product',
    },
  })
}
</script>

<template>
  <div class="CartUpsellAdditional">
    <div class="Stack Stack--tiny">
      <h5>{{ title }}</h5>
      <div class="CartUpsellAdditional__Product">
        <div class="CartUpsellAdditional__Product__Image">
          <img :src="product.image" :alt="product.title" />
        </div>
        <div class="CartUpsellAdditional__Product__Content">
          <p>
            <span>{{ product.title }}</span>
            <br />
            <strong>{{ formatMoney(price) }}</strong
            >&nbsp;<s v-if="price !== selectedVariant.price">{{
              formatMoney(selectedVariant.price)
            }}</s>
          </p>
          <div class="CartUpsellAdditional__Product__Inputs">
            <QuantityControl
              :value="selection.quantity"
              :min-value="1"
              :max-value="10"
              :is-busy="false"
              @change-quantity="selection.quantity = $event"
            ></QuantityControl>
            <Select
              v-if="selectedVariant.sellingPlanAllocations.length > 0"
              :value="selection.selectedSellingPlanId"
              @change="selection.selectedSellingPlanId = $event"
            >
              <option
                v-for="option in selectedVariant.sellingPlanAllocations"
                :value="option.sellingPlan.id"
                :key="option.sellingPlanGroupId"
              >
                {{
                  option.sellingPlan.name
                    .replace(/^delivery /i, '')
                    .replace('1 ', '')
                    .toLowerCase()
                }}
              </option>
              <option :value="null">once only</option>
            </Select>
          </div>
        </div>
      </div>
      <div class="Stack Stack--tiny">
        <Button
          :modifiers="['block']"
          @click="onAdd"
          :loading="isBusy"
          :disabled="isBusy"
        >
          {{ cta.replace('{price}', formatMoney(price).toString()) }}
        </Button>
        <Button :modifiers="['link', 'block']" @click="emit('skip')">
          No thanks, go to checkout
        </Button>
      </div>
    </div>
  </div>
</template>

<style>
.CartUpsellAdditional__Product {
  display: flex;
  gap: 16px;
}

.CartUpsellAdditional__Product__Image {
  width: 80px;

  & img {
    border-radius: var(--radius-small);
  }
}

.CartUpsellAdditional__Product__Content {
  flex-grow: 1;
}

.CartUpsellAdditional__Product__Inputs {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  & .QuantityControl {
    flex-grow: 0;
  }

  & .FormField {
    flex-grow: 1;
  }

  & .FormField__Input.FormField__Input--select {
    font-size: 16px;
    padding: 6px 10px;
  }
}
</style>
